import { isValidUrl, withProtocol } from "@shared/lib/urls"
import { LinkDialogCore } from "@shared/unified-tiptap/editor-ui/link/EditorLinkDialog"
import type { Link } from "@shared/unified-tiptap/extensions/link/link-attrs"
import { linkFromUrl } from "@shared/unified-tiptap/extensions/link/link-inline-extension"
import type { Editor } from "@tiptap/core"
import { Node as PMNode } from "@tiptap/pm/model"
import { useState } from "react"

/**
 * AddButton "Link" dialog.
 *
 * Wrapper around EditorLinkDialog's core display that changes the add logic.
 */
export const AddButtonLinkDialog = ({
  editor,
  open,
  onOpenChange,
  addToCard,
}: {
  editor: Editor
  open: boolean
  onOpenChange: (open: boolean) => void
  addToCard: (node: PMNode, link: Link) => void
}) => {
  const [url, setUrl] = useState("")
  const [title, setTitle] = useState("")
  // The preview-vs-hyperlink picker is hidden for now (see LinkDialogCore), so we default
  // to the "preview" style: it inserts a linkBlock token, which renders as an attachment
  // line while unfurling is deferred. We'll re-enable the picker later.
  const [style, setStyle] = useState<"hyperlink" | "preview">("preview")

  const handleClose = () => {
    onOpenChange(false)
    setUrl("")
    setTitle("")
    // Don't clear style - they'll probably want to use the same style next time.
  }

  const handleAddAsBlock = () => {
    const processedUrl = withProtocol(url.trim())
    const userTitle = title.trim() || null

    // Both styles insert one unified Link token (docs/unified-links.md §6: link dialog
    // path — source "link"), differing only in form.
    const attrs = linkFromUrl(processedUrl, userTitle || processedUrl)
    if (style === "hyperlink") {
      addToCard(
        editor.schema.node("paragraph", null, [editor.schema.node("linkInline", attrs)]),
        attrs,
      )
    } else {
      const blockAttrs = { ...attrs, renderAs: "block" as const }
      addToCard(editor.schema.node("linkBlock", blockAttrs), blockAttrs)
    }
    handleClose()
  }

  const trimmedUrl = url.trim()
  const isValid = trimmedUrl !== "" && isValidUrl(trimmedUrl)

  return (
    <LinkDialogCore
      open={open}
      onOpenChange={onOpenChange}
      mode="Add"
      submitLabel="Add"
      url={url}
      setUrl={setUrl}
      title={title}
      setTitle={setTitle}
      style={style}
      setStyle={setStyle}
      isValid={isValid}
      onClose={handleClose}
      onSubmit={handleAddAsBlock}
    />
  )
}
